"use client"

import Link from "next/link"
import Image from "next/image"
import packageJson from "@/package.json"
import Script from "next/script"
import { logout } from "@/app/actions/auth"
import { useTransition, useState, useEffect } from "react"
import "./splash.css"

type SplashClientProps = {
  user?: {
    name?: string | null
    email?: string | null
    image?: string | null
  } | null
}

export default function SplashClient({ user }: SplashClientProps) {
  const [isPending, startTransition] = useTransition()
  const [isBetaActive, setIsBetaActive] = useState(false)

  // Fetch beta status from API endpoint
  useEffect(() => {
    fetch('/api/public/beta-status')
      .then(res => res.json())
      .then(data => setIsBetaActive(data.isBetaActive))
      .catch(err => console.error('Failed to fetch beta status:', err))
  }, [])

  const handleLogout = () => {
    startTransition(async () => {
      await logout()
    })
  }

  const carouselImages = [
    'image1.jpg',
    'image2.png',
    'image3.jpg',
    'image4.jpg',
    'image5.jpg',
    'image6.jpg',
    'image7.jpg',
    'image8.jpg'
  ]

  return (
    <div className="relative min-h-screen">
      {/* Background Carousel */}
      <div className="carousel-bg" id="carousel-bg">
        {carouselImages.map((image, index) => (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            key={image}
            src={`/carousel/${image}`}
            alt=""
            className={index === 0 ? 'active' : ''}
            loading={index < 3 ? "eager" : "lazy"}
            fetchPriority={index === 0 ? "high" : "low"}
          />
        ))}
      </div>

      <Script id="carousel-script" strategy="afterInteractive">
        {`
          (function() {
            const images = document.querySelectorAll('#carousel-bg img');
            let currentIndex = 0;

            setInterval(() => {
              images[currentIndex].classList.remove('active');
              currentIndex = (currentIndex + 1) % images.length;
              images[currentIndex].classList.add('active');
            }, 5000);
          })();
        `}
      </Script>

      {/* Content */}
      <div className="relative z-10 min-h-screen flex flex-col justify-center items-center px-4">
        {/* Logo */}
        <div className="mb-8 text-center">
          <Image
            src="/logo.png"
            alt="PadelManager Logo"
            width={256}
            height={256}
            className="mx-auto mb-4"
            priority
          />
          <Image
            src="/logo_text.png"
            alt="PadelManager"
            width={256}
            height={64}
            className="mx-auto"
            priority
          />
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-4">
          {user ? (
            <>
              <Link
                href="/dashboard"
                className="inline-block w-32 px-6 py-2 bg-chart-2 text-primary-foreground font-semibold rounded-lg hover:bg-chart-2 text-center transition"
              >
                Dashboard
              </Link>
              <span className="text-muted-foreground">|</span>
              <button
                onClick={handleLogout}
                disabled={isPending}
                className="inline-block w-32 px-2 py-2 bg-destructive text-primary-foreground font-semibold rounded-lg hover:bg-destructive text-center transition cursor-pointer disabled:opacity-50"
              >
                {isPending ? "Logout..." : "Logout"}
              </button>
            </>
          ) : (
            <>
              <Link
                href="/auth/signin"
                className="inline-block w-32 px-6 py-2 bg-chart-2 text-primary-foreground font-semibold rounded-lg hover:bg-chart-2 text-center transition"
              >
                Login
              </Link>
              <span className="text-muted-foreground">|</span>
              <Link
                href="/auth/signup"
                className="inline-block w-32 px-2 py-2 bg-primary text-primary-foreground font-semibold rounded-lg hover:bg-primary text-center transition"
              >
                Registrati
              </Link>
            </>
          )}
        </div>

        {/* Version */}
        <div className="flex items-center gap-2 mt-4">
          <p className="text-muted-foreground text-sm text-center">
            Vers. {packageJson.version}
          </p>
          {isBetaActive && (
            <Link
              href="/beta"
              className="bg-gradient-to-r from-orange-500 to-red-500 text-primary-foreground text-xs font-bold px-2.5 py-1 rounded-full shadow-lg hover:shadow-xl transition-all hover:scale-105 animate-pulse"
              title="Informazioni versione beta"
            >
              BETA
            </Link>
          )}
        </div>
      </div>
    </div>
  )
}